/** FormData builder for MedCloud upload — no apiSlice/store imports (avoids circular deps). */

function readStoredUserData() {
  try {
    const raw =
      localStorage.getItem("userData") ||
      sessionStorage.getItem("userData") ||
      "{}";
    return JSON.parse(raw);
  } catch {
    return {};
  }
}

function appendFormField(formData, key, value) {
  if (value == null) return;
  const trimmed = String(value).trim();
  if (trimmed) {
    formData.append(key, trimmed);
  }
}

function resolvePatientId(raw) {
  if (raw == null || raw === "") return null;
  const text = String(raw).trim();
  if (
    !text ||
    text.toLowerCase() === "null" ||
    text.toLowerCase() === "undefined"
  ) {
    return null;
  }
  const num = Number(text);
  if (!Number.isFinite(num) || num <= 0) return null;
  return String(Math.trunc(num));
}

function mimeToExtension(mime) {
  const map = {
    "application/pdf": "pdf",
    "image/jpeg": "jpg",
    "image/jpg": "jpg",
    "image/png": "png",
    "image/gif": "gif",
    "image/webp": "webp",
    "video/mp4": "mp4",
    "video/quicktime": "mov",
    "video/webm": "webm",
  };
  return map[(mime || "").toLowerCase()] ?? null;
}

export function normalizeMedCloudFile(file) {
  const name = (file.name || "").trim();
  if (name?.includes(".") && file.size > 0) {
    return file;
  }

  const fromMime = mimeToExtension(file.type || "");
  const fromName = name.includes(".")
    ? name.split(".").pop()?.toLowerCase()
    : null;
  const ext = fromMime || fromName || "pdf";
  const safeName =
    name?.includes(".") ? name : `medcloud_${Date.now()}.${ext}`;

  return new File([file], safeName, {
    type: file.type || "application/octet-stream",
    lastModified: file.lastModified || Date.now(),
  });
}

export function buildMedCloudUploadFormData(payload) {
  const formData = new FormData();
  const file = normalizeMedCloudFile(payload.file);
  formData.append("file", file);

  const userId = (payload.user_id ?? "").trim();
  if (!userId) {
    throw new Error("MEDCLOUD_MISSING_USER_ID");
  }
  formData.append("user_id", userId);

  const patientId = resolvePatientId(payload.patient_id);
  if (patientId) {
    formData.append("patient_id", patientId);
  }

  appendFormField(formData, "session_id", payload.session_id);
  appendFormField(formData, "tags", payload.tags);

  const lang =
    (payload.language ?? "en").trim().split("-")[0].toLowerCase() || "en";
  formData.append("language", lang);

  if (payload.profile_upload !== undefined) {
    formData.append("profile_upload", String(payload.profile_upload));
  }

  return formData;
}

export function resolveMedCloudUserId(userData) {
  const primary = readStoredUserData();
  const uid =
    userData?.userId ??
    userData?.user_id ??
    userData?.id ??
    primary?.userId ??
    primary?.user_id ??
    primary?.id;
  return uid != null && String(uid).trim() ? String(uid).trim() : "";
}

export { resolvePatientId };
